import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { Button, Group } from '@mantine/core';

/**
 * Контекст для CleanTabs — хранит value и onChange.
 */
interface CleanTabsContextValue {
  value: string | null;
  onChange: (value: string) => void;
}

const CleanTabsContext = createContext<CleanTabsContextValue | null>(null);

function useCleanTabsContext(): CleanTabsContextValue {
  const ctx = useContext(CleanTabsContext);
  if (!ctx) {
    throw new Error('CleanTabs.Tab и CleanTabs.Panel должны использоваться внутри CleanTabs');
  }
  return ctx;
}

export interface CleanTabsProps {
  /** Текущее значение активной вкладки */
  value: string | null;
  /** Обработчик смены вкладки */
  onChange: (value: string) => void;
  /** Дочерние элементы: CleanTabs.List и CleanTabs.Panel */
  children: ReactNode;
  /** Дополнительные стили корневого контейнера */
  style?: React.CSSProperties;
  className?: string;
}

/**
 * CleanTabs — компонент вкладок в виде ссылок (link-style).
 * Предназначен для дальнейшей стилизации.
 */
export function CleanTabs({ value, onChange, children, style, className }: CleanTabsProps) {
  return (
    <CleanTabsContext.Provider value={{ value, onChange }}>
      <div
        className={className}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          paddingLeft: 0,
          marginLeft: 0,
          ...style,
        }}
      >
        {children}
      </div>
    </CleanTabsContext.Provider>
  );
}

export interface CleanTabsListProps {
  /** Вкладки: CleanTabs.Tab */
  children: ReactNode;
  /**
   * Контент справа от списка вкладок (например, toggle-кнопка панели Activity).
   * Прижимается к правому краю, табы остаются слева.
   */
  rightSection?: ReactNode;
  /** Дополнительные стили */
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Список вкладок — рендерит кнопки в стиле ссылок.
 * Если задан rightSection — табы прижимаются влево, rightSection вправо.
 */
export function CleanTabsList({ children, rightSection, style, className }: CleanTabsListProps) {
  return (
    <Group
      gap={0}
      wrap='nowrap'
      justify={rightSection ? 'space-between' : undefined}
      style={{
        paddingLeft: 0,
        marginLeft: 0,
        marginBottom: 'var(--mantine-spacing-xs)',
        overflow: 'hidden',
        minWidth: 0,
        ...style,
      }}
      className={className}
    >
      <Group gap={0} wrap='nowrap' style={{ overflow: 'hidden', minWidth: 0 }}>
        {children}
      </Group>
      {rightSection ? <div style={{ flexShrink: 0 }}>{rightSection}</div> : null}
    </Group>
  );
}

export interface CleanTabsTabProps {
  /** Значение вкладки */
  value: string;
  /** Текст/контент вкладки */
  children: ReactNode;
}

/**
 * Одна вкладка — кнопка в стиле ссылки.
 */
export function CleanTabsTab({ value, children }: CleanTabsTabProps) {
  const { value: activeValue, onChange } = useCleanTabsContext();
  const isActive = activeValue === value;

  const tabColor = '#406ABF';

  return (
    <Button
      variant={isActive ? 'light' : 'subtle'}
      size='xs'
      color='blue'
      onClick={() => onChange(value)}
      styles={{
        root: {
          fontWeight: 400,
          color: isActive ? '#000' : tabColor,
          ...(isActive && {
            backgroundColor: '#F2F2F2',
          }),
        },
      }}
    >
      {children}
    </Button>
  );
}

export interface CleanTabsPanelProps {
  /** Значение вкладки, при которой показывается панель */
  value: string;
  /** Контент панели */
  children: ReactNode;
  /** Дополнительные стили */
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Панель вкладки — показывается только когда активна соответствующая вкладка.
 */
export function CleanTabsPanel({ value, children, style, className }: CleanTabsPanelProps) {
  const { value: activeValue } = useCleanTabsContext();

  if (activeValue !== value) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

CleanTabs.List = CleanTabsList;
CleanTabs.Tab = CleanTabsTab;
CleanTabs.Panel = CleanTabsPanel;
